/**
* @summary - Component that Provides Custom Select functionality
* @autor vengateshwaran k
* @since 2024-05-30
*/
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  FormControl,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import { InputAutoComplete } from "../../assets/styled-components/textField";

const CustomSelectInput = ({
  id,
  label,
  options,
  onChange,
  value,
  isMandatoryField = false,
  sx,
  disabled,
  error,
  errorMessage,
  defaultOption,
  helperText,
  disableClearable,
  autoFocus = false,
  inputRef,
  name,
  modal,
  fullWidth = true,
  labelSize,
  highlight,
  disablePortal = false
}) => {
  const viewEnabled = useSelector(
    (state) => state.mandatoryFields.isViewEnabled
  );
  const mandatoryRequiredButton = useSelector(
    (state) => state.mandatoryFields.isMandatoryRequiredEnabled
  );

  const [selectValue, setSelectValue] = useState(value || null);

  const handleInputChange = (newValue) => {
    setSelectValue(newValue);
    onChange(newValue, name, isMandatoryField);
  };

  useMemo(() => {
    // Update the selected value if defaultValue changes externally
    setSelectValue(value || null);
  }, [value]);

  return (
    <>
      <Typography
        variant="labelSm"
        fontSize={labelSize}
        color={"text.primary"}
        component={"label"}
      >
        {label}
        {isMandatoryField && (
          <Typography component={"span"} color={"error"}>
            *
          </Typography>
        )}
      </Typography>
      <FormControl fullWidth={fullWidth} variant="outlined">
        <Autocomplete
          disableClearable={disableClearable}
          theme={InputAutoComplete} // Make sure this is correctly configured
          value={selectValue}
          defaultValue={defaultOption || null} // Ensure defaultOption is handled properly
          disabled={
            disabled ||
            viewEnabled ||
            (mandatoryRequiredButton && !isMandatoryField)
          }
          onChange={(event, newValue) => handleInputChange(newValue)}
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          } // Ensure this comparison is correct
          id={id}
          options={options}
          disablePortal={disablePortal}
          getOptionLabel={(option) => option?.label || ""} // Ensure getOptionLabel returns a string
          sx={sx}
          clearIcon={null}
          slotProps={{
            popper: {
              className: modal && "zIndex-1400",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={disabled?"":"Please Select"}
              variant="outlined"
              name={name}
              sx={{
                '& .MuiInputBase-input': {
                  fontWeight: highlight && 700
                },
              }}
              required={isMandatoryField}
              autoFocus={autoFocus}
              error={error}
              helperText={helperText || errorMessage}
              inputRef={inputRef}
            />
          )}
        />
      </FormControl>
    </>
  );
};

export default CustomSelectInput;
